import { getLoteChipStatus } from '../components/lotes/LoteChip';
import type { LoteAsignacion } from '../services/loteService';
import kitService from '../services/kitService';
import productoService from '../services/productoService';

export type LoteMode = 'ingreso' | 'egreso' | 'transferencia';

export function normalizeControlLoteSerie(value: unknown): string {
  return String(value ?? 'N').trim().toUpperCase() || 'N';
}

/** Igual que backend GetControlMode: inventariado=1 y no SER. */
export function productoEsInventariadoParaLote(productoOrFlag: unknown): boolean {
  if (productoOrFlag == null) return false;
  if (typeof productoOrFlag === 'object') {
    const p = productoOrFlag as Record<string, unknown>;
    const tipo = String(p.tipoArticulo ?? p.TipoArticulo ?? '').trim().toUpperCase();
    if (tipo === 'SER') return false;
    return Number(p.invetariado ?? p.Invetariado ?? 0) === 1;
  }
  return Number(productoOrFlag) === 1;
}

export function productoControlaLote(controlLoteSerie: unknown): boolean {
  const c = normalizeControlLoteSerie(controlLoteSerie);
  return c === 'L' || c === 'S';
}

export function esControlSerie(controlLoteSerie: unknown): boolean {
  return normalizeControlLoteSerie(controlLoteSerie) === 'S';
}

/**
 * Flags efectivos alineados al backend: si no está inventariado (o es SER),
 * se fuerza ControlLoteSerie = N para que la UI no pida lotes.
 */
export function extractLoteFlagsFromProducto(producto: unknown): {
  controlLoteSerie: string;
  requiereVencimiento: boolean;
} {
  const p = (producto ?? {}) as Record<string, unknown>;
  const raw = normalizeControlLoteSerie(p.controlLoteSerie ?? p.ControlLoteSerie ?? 'N');
  const controla =
    productoEsInventariadoParaLote(p) && (raw === 'L' || raw === 'S') ? raw : 'N';
  return {
    controlLoteSerie: controla,
    requiereVencimiento: Boolean(p.requiereVencimiento ?? p.RequiereVencimiento),
  };
}

export function mapLotesFromApiRaw(raw: unknown): LoteAsignacion[] {
  const list = Array.isArray(raw) ? raw : [];
  return list
    .map((l) => {
      const row = (l ?? {}) as Record<string, unknown>;
      return {
        idLote: (row.idLote ?? row.IdLote ?? null) as number | null,
        nroLote: String(row.nroLote ?? row.NroLote ?? '').trim() || null,
        fechaVencimiento: (row.fechaVencimiento ?? row.FechaVencimiento ?? null) as string | null,
        fechaFabricacion: (row.fechaFabricacion ?? row.FechaFabricacion ?? null) as string | null,
        cantidad: Number(row.cantidad ?? row.Cantidad ?? 0),
        observaciones: (row.observaciones ?? row.Observaciones ?? null) as string | null,
      } satisfies LoteAsignacion;
    })
    .filter((l) => Number.isFinite(l.cantidad) && l.cantidad > 0);
}

/** Si la factura trae un solo lote, lo asigna con la cantidad de la línea NC. */
export function autoAsignarLoteUnico(
  cantidad: number,
  lotesOrigen: LoteAsignacion[] | null | undefined,
): LoteAsignacion[] | undefined {
  const qty = Number(cantidad) || 0;
  if (qty <= 0 || !lotesOrigen || lotesOrigen.length !== 1) return undefined;
  return [{ ...lotesOrigen[0], cantidad: qty }];
}

export interface KitLoteComponenteLinea {
  codigoArticulo: string;
  descripcion?: string | null;
  cantidadPorKit: number;
  controlLoteSerie?: string | null;
  requiereVencimiento?: boolean;
  lotes?: LoteAsignacion[] | null;
}

export interface LineaConLote {
  codigoArticulo?: string | null;
  cantidad?: number | null;
  controlLoteSerie?: string | null;
  lotes?: LoteAsignacion[] | null;
  esKit?: boolean;
  lotesComponentes?: KitLoteComponenteLinea[] | null;
}

/** Devuelve mensaje de error si hay renglones con lote incompleto/vencido; null si OK. */
export function mensajeValidacionLotes(
  items: LineaConLote[],
  opts?: { soloConCodigo?: boolean },
): string | null {
  const soloConCodigo = opts?.soloConCodigo !== false;
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    const codigo = String(it.codigoArticulo ?? '').trim();
    if (soloConCodigo && !codigo) continue;
    const cantidad = Number(it.cantidad) || 0;
    if (cantidad <= 0) continue;

    if (it.esKit && Array.isArray(it.lotesComponentes)) {
      for (const c of it.lotesComponentes) {
        if (!productoControlaLote(c.controlLoteSerie)) continue;
        const qtyComp = (Number(c.cantidadPorKit) || 0) * cantidad;
        if (qtyComp <= 0) continue;
        const status = getLoteChipStatus(true, qtyComp, c.lotes);
        if (status !== 'ok') {
          const detalle =
            status === 'expired'
              ? 'tiene lote vencido'
              : 'tiene lotes incompletos o sin asignar';
          return `El componente "${c.codigoArticulo}" del kit "${codigo || `#${i + 1}`}" ${detalle}. Asigná los lotes antes de guardar.`;
        }
      }
      continue;
    }

    if (!productoControlaLote(it.controlLoteSerie)) continue;
    const status = getLoteChipStatus(true, cantidad, it.lotes);
    if (status !== 'ok') {
      const detalle =
        status === 'expired'
          ? 'tiene lote vencido'
          : 'tiene lotes incompletos o sin asignar';
      return `El artículo "${codigo || `#${i + 1}`}" ${detalle}. Asigná los lotes antes de guardar.`;
    }
  }
  return null;
}

export function mapLotesComponentesPayload(
  lotesComponentes: KitLoteComponenteLinea[] | null | undefined,
): { codigoArticulo: string; lotes: LoteAsignacion[] }[] {
  return (lotesComponentes ?? [])
    .filter((c) => productoControlaLote(c.controlLoteSerie) && c.lotes && c.lotes.length > 0)
    .map((c) => ({
      codigoArticulo: String(c.codigoArticulo).trim(),
      lotes: c.lotes ?? [],
    }));
}

/** Hidrata componentes de kit con flags de lote (solo si el artículo es KIT). */
export async function buildKitLotesComponentes(
  codigoKit: string,
  producto?: unknown,
): Promise<{ esKit: boolean; lotesComponentes?: KitLoteComponenteLinea[] }> {
  const codigo = String(codigoKit ?? '').trim();
  if (!codigo) return { esKit: false };
  const p = (producto ?? {}) as Record<string, unknown>;
  const tipo = String(p.tipoArticulo ?? p.TipoArticulo ?? '').trim().toUpperCase();
  if (tipo && tipo !== 'KIT') return { esKit: false };

  try {
    const kit = await kitService.getKit(codigo);
    if (!kit) return { esKit: tipo === 'KIT', lotesComponentes: tipo === 'KIT' ? [] : undefined };

    const lotesComponentes: KitLoteComponenteLinea[] = await Promise.all(
      (kit.componentes ?? []).map(async (c) => {
        let flags = { controlLoteSerie: 'N', requiereVencimiento: false };
        try {
          const prod = await productoService.getProducto(c.codigoArticulo);
          flags = extractLoteFlagsFromProducto(prod);
        } catch {
          /* N */
        }
        return {
          codigoArticulo: String(c.codigoArticulo).trim(),
          descripcion: c.descripcion ?? null,
          cantidadPorKit: Number(c.cantidad) || 0,
          controlLoteSerie: flags.controlLoteSerie,
          requiereVencimiento: flags.requiereVencimiento,
          lotes: undefined,
        };
      }),
    );
    return { esKit: true, lotesComponentes };
  } catch {
    return { esKit: tipo === 'KIT', lotesComponentes: tipo === 'KIT' ? [] : undefined };
  }
}
